import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Copy, Check, ChevronDown, Trash2, Eye, EyeOff, AlertTriangle } from "lucide-react";
import {
  useApiKeys,
  useCreateApiKey,
  useRevokeApiKey,
  type ApiPermissionLevel,
} from "@/hooks/useApiKeys";
import { useOAuthConnectedApps, useRevokeOAuthApp } from "@/hooks/useOAuthConsent";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
// Same catalogue the `api`/`mcp` edge functions enforce — single source of truth.
import { OPERATIONS } from "../../../supabase/functions/_shared/dashboard/operations.ts";

// Same URL the app itself talks to (works for custom domains / self-hosted,
// unlike rebuilding it from the project id).
const FUNCTIONS_URL = `${String(import.meta.env.VITE_SUPABASE_URL ?? "").replace(/\/$/, "")}/functions/v1`;
const BASE_URL = `${FUNCTIONS_URL}/api`;
const MCP_URL = `${FUNCTIONS_URL}/mcp`;

type Method = "GET" | "POST" | "PUT" | "DELETE";

const methodPill: Record<Method, string> = {
  GET: "bg-ok-soft text-ok",
  POST: "bg-accent text-primary",
  PUT: "bg-warn-soft text-warn",
  DELETE: "bg-bad-soft text-bad",
};

const permissionLabels: Record<string, string> = {
  read: "Leitura",
  write: "Escrita",
  admin: "Admin",
};

const sectionCls = "bg-card border border-border rounded-2xl shadow-card";
const sectionHeadCls =
  "px-6 pt-[18px] pb-3.5 border-b border-border/70 flex items-center justify-between";
const h2Cls = "m-0 text-[15px] font-bold";
const smallPrimaryBtn = "h-8 px-[13px] rounded-lg text-xs font-semibold";
const pillCls = "inline-flex items-center h-[23px] px-2.5 rounded-[7px] text-[11px] font-bold";

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-[11px] border border-border bg-muted/60 px-[15px] py-[13px] font-mono text-[12.5px] text-muted-foreground">
        <code>{code}</code>
      </pre>
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-1.5 top-1.5 h-7 w-7"
        onClick={handleCopy}
        aria-label="Copiar"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}

interface EndpointDef {
  method: Method;
  path: string;
  description: string;
  minPermission: "read" | "write" | "admin";
  body?: string;
  response?: string;
}

function EndpointRow({ method, path, description, minPermission, body, response }: EndpointDef) {
  const [open, setOpen] = useState(false);
  const fullUrl = `${BASE_URL}${path}`;
  const hasDetails = !!(body || response);

  return (
    <div className="border-b border-border/70 last:border-b-0">
      <button
        type="button"
        onClick={() => hasDetails && setOpen((o) => !o)}
        className={`flex w-full items-center gap-4 px-6 py-3.5 text-left ${hasDetails ? "cursor-pointer hover:bg-muted/40" : "cursor-default"}`}
      >
        <span
          className={`inline-flex h-[23px] w-[66px] shrink-0 items-center justify-center rounded-[7px] text-[10.5px] font-bold ${methodPill[method]}`}
        >
          {method}
        </span>
        <span className="w-[150px] shrink-0 truncate font-mono text-[12.5px] font-semibold">
          {path}
        </span>
        <span className="min-w-0 flex-1 truncate text-[12.5px] text-muted-foreground">
          {description}
        </span>
        <span className="hidden shrink-0 text-[11px] text-muted-foreground sm:inline">
          {permissionLabels[minPermission]}
        </span>
        {hasDetails && (
          <ChevronDown
            className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          />
        )}
      </button>
      {open && (
        <div className="space-y-3 px-6 pb-4">
          <div>
            <p className="mb-1 text-[11px] font-semibold text-muted-foreground">URL completa</p>
            <CodeBlock code={fullUrl} />
          </div>
          {body && (
            <div>
              <p className="mb-1 text-[11px] font-semibold text-muted-foreground">Request body</p>
              <CodeBlock code={body} />
            </div>
          )}
          {response && (
            <div>
              <p className="mb-1 text-[11px] font-semibold text-muted-foreground">Response</p>
              <CodeBlock code={response} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ApiKeysSection() {
  const { data: apiKeys, isLoading } = useApiKeys();
  const createApiKey = useCreateApiKey();
  const revokeApiKey = useRevokeApiKey();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyPermission, setNewKeyPermission] = useState<ApiPermissionLevel>("read");
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(false);

  const handleCreateKey = async () => {
    const result = await createApiKey.mutateAsync({
      name: newKeyName,
      permissions: [newKeyPermission],
    });
    setCreatedKey(result.fullKey);
    setNewKeyName("");
    setNewKeyPermission("read");
  };

  const handleCopyKey = () => {
    if (createdKey) navigator.clipboard.writeText(createdKey);
  };
  const handleCloseCreatedDialog = () => {
    setCreatedKey(null);
    setShowCreateDialog(false);
    setShowKey(false);
  };

  return (
    <section className={`${sectionCls} overflow-hidden`}>
      <div className={sectionHeadCls}>
        <h2 className={h2Cls}>Chaves de API</h2>
        <Button className={smallPrimaryBtn} onClick={() => setShowCreateDialog(true)}>
          Gerar chave
        </Button>
      </div>

      {isLoading ? (
        <div className="py-6 text-center text-sm text-muted-foreground">Carregando...</div>
      ) : !apiKeys || apiKeys.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          Nenhuma chave criada ainda.
        </div>
      ) : (
        apiKeys.map((key) => {
          const perms = Array.isArray(key.permissions) ? key.permissions : [];
          const level = perms.includes("admin")
            ? "admin"
            : perms.includes("write")
              ? "write"
              : "read";
          const used = key.last_used_at
            ? `usada ${formatDistanceToNow(new Date(key.last_used_at), { addSuffix: true, locale: ptBR })}`
            : `criada ${formatDistanceToNow(new Date(key.created_at), { addSuffix: true, locale: ptBR })}`;
          return (
            <div
              key={key.id}
              className="flex items-center gap-4 border-b border-border/70 px-6 py-[15px] last:border-b-0 hover:bg-muted/40"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13.5px] font-semibold">{key.name}</div>
                <div className="mt-0.5 font-mono text-[11.5px] text-muted-foreground">
                  {key.key_prefix}…
                </div>
              </div>
              <span className={`${pillCls} bg-accent text-primary`}>{permissionLabels[level]}</span>
              <span className="hidden text-[11.5px] text-muted-foreground sm:inline">{used}</span>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-bad"
                    aria-label="Revogar chave"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Revogar chave de API?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Esta ação não pode ser desfeita. Integrações que usam esta chave param de
                      funcionar.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => revokeApiKey.mutate(key.id)}
                      className="bg-bad text-bad-foreground hover:bg-bad/90"
                    >
                      Revogar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          );
        })
      )}

      <Dialog open={showCreateDialog && !createdKey} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gerar chave de API</DialogTitle>
            <DialogDescription>Defina o nome e o nível de permissão da chave.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="key-name">Nome da chave</Label>
              <Input
                id="key-name"
                placeholder="Ex: Integração externa"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="api-key-nivel-de-permissao">Nível de permissão</Label>
              <Select
                value={newKeyPermission}
                onValueChange={(v) => setNewKeyPermission(v as ApiPermissionLevel)}
              >
                <SelectTrigger id="api-key-nivel-de-permissao">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="read">Leitura — apenas GET</SelectItem>
                  <SelectItem value="write">Escrita — GET + POST + PUT</SelectItem>
                  <SelectItem value="admin">Admin — acesso total + DELETE</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateKey}
              disabled={!newKeyName.trim() || createApiKey.isPending}
            >
              Gerar chave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!createdKey} onOpenChange={handleCloseCreatedDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warn" />
              Chave gerada
            </DialogTitle>
            <DialogDescription>
              <strong className="text-bad">Atenção:</strong> esta chave será mostrada apenas uma
              vez.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="relative">
              <Input
                type={showKey ? "text" : "password"}
                value={createdKey || ""}
                readOnly
                className="pr-20 font-mono text-sm"
              />
              <div className="absolute right-1 top-1 flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setShowKey(!showKey)}
                  aria-label="Mostrar chave"
                >
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleCopyKey}
                  aria-label="Copiar chave"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleCloseCreatedDialog}>Entendi, já copiei</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

// --- Resource definitions ---
interface ResourceSection {
  key: string;
  label: string;
  endpoints: EndpointDef[];
}

const resources: ResourceSection[] = [
  {
    key: "leads",
    label: "Leads",
    endpoints: [
      {
        method: "GET",
        path: "/v1/leads",
        description: "Lista todos os leads com paginação. Params: ?page=1&limit=50",
        minPermission: "read",
        response:
          '{ "success": true, "data": [...], "meta": { "total": 42, "page": 1, "limit": 50 } }',
      },
      {
        method: "GET",
        path: "/v1/leads/{id}",
        description: "Retorna um lead específico pelo ID.",
        minPermission: "read",
        response:
          '{ "success": true, "data": { "id": "uuid", "name": "João", "email": "joao@ex.com", "status": "new" } }',
      },
      {
        method: "POST",
        path: "/v1/leads",
        description: "Cria um novo lead.",
        minPermission: "write",
        body: '{\n  "name": "João Silva",\n  "email": "joao@ex.com",\n  "message": "Preciso de um site",\n  "phone": "+5538999999999",\n  "source": "api"\n}',
        response: '{ "success": true, "data": { "id": "uuid", ... } }',
      },
      {
        method: "PUT",
        path: "/v1/leads/{id}",
        description: "Atualiza um lead existente.",
        minPermission: "write",
        body: '{\n  "status": "contacted",\n  "notes": "Retornei por email"\n}',
      },
      {
        method: "DELETE",
        path: "/v1/leads/{id}",
        description: "Remove um lead.",
        minPermission: "admin",
      },
    ],
  },
  {
    key: "interactions",
    label: "Interações de leads",
    endpoints: [
      {
        method: "GET",
        path: "/v1/leads/{lead_id}/interactions",
        description: "Lista interações de um lead.",
        minPermission: "read",
      },
      {
        method: "POST",
        path: "/v1/leads/{lead_id}/interactions",
        description: "Registra uma interação (email, whatsapp, call, note).",
        minPermission: "write",
        body: '{\n  "type": "email",\n  "content": "Enviado proposta por email"\n}',
      },
    ],
  },
  {
    key: "blog-posts",
    label: "Blog",
    endpoints: [
      {
        method: "GET",
        path: "/v1/blog-posts",
        description: "Lista todos os posts do blog.",
        minPermission: "read",
      },
      {
        method: "GET",
        path: "/v1/blog-posts/{id}",
        description: "Retorna um post específico.",
        minPermission: "read",
      },
      {
        method: "POST",
        path: "/v1/blog-posts",
        description: "Cria um novo post.",
        minPermission: "write",
        body: '{\n  "title": "Novo Post",\n  "slug": "novo-post",\n  "content": "Conteúdo do post...",\n  "excerpt": "Resumo",\n  "published": false\n}',
      },
      {
        method: "PUT",
        path: "/v1/blog-posts/{id}",
        description: "Atualiza um post.",
        minPermission: "write",
        body: '{ "published": true, "published_at": "2025-01-15T00:00:00Z" }',
      },
      {
        method: "DELETE",
        path: "/v1/blog-posts/{id}",
        description: "Remove um post.",
        minPermission: "admin",
      },
    ],
  },
  {
    key: "categories",
    label: "Categorias",
    endpoints: [
      {
        method: "GET",
        path: "/v1/categories",
        description: "Lista todas as categorias do blog.",
        minPermission: "read",
      },
      {
        method: "GET",
        path: "/v1/categories/{id}",
        description: "Retorna uma categoria.",
        minPermission: "read",
      },
      {
        method: "POST",
        path: "/v1/categories",
        description: "Cria uma nova categoria.",
        minPermission: "write",
        body: '{\n  "name": "React",\n  "slug": "react",\n  "description": "Posts sobre React"\n}',
      },
      {
        method: "PUT",
        path: "/v1/categories/{id}",
        description: "Atualiza uma categoria.",
        minPermission: "write",
      },
      {
        method: "DELETE",
        path: "/v1/categories/{id}",
        description: "Remove uma categoria.",
        minPermission: "admin",
      },
    ],
  },
  {
    key: "projects",
    label: "Portfólio",
    endpoints: [
      {
        method: "GET",
        path: "/v1/projects",
        description: "Lista projetos do portfólio.",
        minPermission: "read",
      },
      {
        method: "GET",
        path: "/v1/projects/{id}",
        description: "Retorna um projeto.",
        minPermission: "read",
      },
      {
        method: "POST",
        path: "/v1/projects",
        description: "Cria um projeto.",
        minPermission: "write",
        body: '{\n  "title": "App Mobile",\n  "description": "App para delivery",\n  "technologies": ["React Native", "Node.js"],\n  "status": "draft"\n}',
      },
      {
        method: "PUT",
        path: "/v1/projects/{id}",
        description: "Atualiza um projeto.",
        minPermission: "write",
      },
      {
        method: "DELETE",
        path: "/v1/projects/{id}",
        description: "Remove um projeto.",
        minPermission: "admin",
      },
    ],
  },
  {
    key: "client-projects",
    label: "Projetos de clientes",
    endpoints: [
      {
        method: "GET",
        path: "/v1/client-projects",
        description: "Lista projetos de clientes.",
        minPermission: "read",
      },
      {
        method: "GET",
        path: "/v1/client-projects/{id}",
        description: "Retorna um projeto de cliente.",
        minPermission: "read",
      },
      {
        method: "POST",
        path: "/v1/client-projects",
        description: "Cria um projeto de cliente.",
        minPermission: "write",
        body: '{\n  "title": "Site Corporativo",\n  "client_name": "Empresa X",\n  "client_email": "contato@empresa.com",\n  "phase": "backlog",\n  "budget": 5000\n}',
      },
      {
        method: "PUT",
        path: "/v1/client-projects/{id}",
        description: "Atualiza fase, prioridade, etc.",
        minPermission: "write",
        body: '{ "phase": "in_progress", "priority": 1 }',
      },
      {
        method: "DELETE",
        path: "/v1/client-projects/{id}",
        description: "Remove um projeto de cliente.",
        minPermission: "admin",
      },
    ],
  },
  {
    key: "analytics",
    label: "Analytics",
    endpoints: [
      {
        method: "GET",
        path: "/v1/analytics/events",
        description: "Lista eventos de analytics com paginação.",
        minPermission: "read",
      },
      {
        method: "POST",
        path: "/v1/analytics/events",
        description: "Registra um evento de analytics.",
        minPermission: "write",
        body: '{\n  "event_type": "page_view",\n  "page_path": "/blog",\n  "referrer": "https://google.com"\n}',
      },
      {
        method: "GET",
        path: "/v1/analytics/summary",
        description: "Retorna resumo agregado dos eventos.",
        minPermission: "read",
        response:
          '{ "success": true, "data": { "total_events": 1523, "by_type": { "page_view": 1200, "form_submit": 323 } } }',
      },
    ],
  },
  {
    key: "notifications",
    label: "Notificações",
    endpoints: [
      {
        method: "GET",
        path: "/v1/notifications",
        description: "Lista notificações do admin.",
        minPermission: "read",
      },
      {
        method: "PUT",
        path: "/v1/notifications/{id}/read",
        description: "Marca uma notificação como lida.",
        minPermission: "write",
      },
      {
        method: "PUT",
        path: "/v1/notifications/read-all",
        description: "Marca todas como lidas.",
        minPermission: "write",
      },
    ],
  },
  {
    key: "prospects",
    label: "Leads frios (prospecção)",
    endpoints: [
      {
        method: "GET",
        path: "/v1/prospect-lists",
        description: "Lista as listas de leads frios com total e quantos foram para o funil.",
        minPermission: "read",
      },
      {
        method: "GET",
        path: "/v1/prospect-lists/{id}",
        description: "Lista com suas colunas personalizadas (key, label, type, options).",
        minPermission: "read",
      },
      {
        method: "POST",
        path: "/v1/prospect-lists",
        description: "Cria uma lista.",
        minPermission: "write",
        body: '{ "name": "Apify — Três Marias", "source": "apify" }',
      },
      {
        method: "POST",
        path: "/v1/prospect-lists/{id}/fields",
        description:
          "Cria coluna: text, long_text, number, currency, url, email, phone, date, boolean, select, multi_select, rating.",
        minPermission: "write",
        body: '{\n  "label": "Temperatura",\n  "type": "select",\n  "options": [{ "value": "Quente", "label": "Quente" }]\n}',
      },
      {
        method: "DELETE",
        path: "/v1/prospect-fields/{id}",
        description: "Exclui a coluna e os valores dela.",
        minPermission: "admin",
      },
      {
        method: "GET",
        path: "/v1/prospects?list_id=&status=&search=",
        description: "Lista leads frios com filtros e paginação.",
        minPermission: "read",
      },
      {
        method: "POST",
        path: "/v1/prospects/batch",
        description:
          "Importa até 500 linhas (chaves livres). Cria lista e colunas automaticamente; reimportar pelo ID externo atualiza sem duplicar.",
        minPermission: "write",
        body: '{\n  "list_name": "Apify — Três Marias",\n  "rows": [{ "title": "Barbearia Karter", "phone": "(38) 98844-5180", "placeId": "ChIJ…", "totalScore": 4.8 }]\n}',
        response:
          '{ "success": true, "data": { "inserted": 1, "updated": 0, "created_fields": ["totalscore"], "errors": [] } }',
      },
      {
        method: "PUT",
        path: "/v1/prospects/{id}",
        description: "Atualiza campos; `data` é mesclado (null apaga uma coluna).",
        minPermission: "write",
        body: '{ "status": "qualified", "data": { "temperatura": "Quente" } }',
      },
      {
        method: "POST",
        path: "/v1/prospects/{id}/promote",
        description: "Move para o funil: cria lead (status Novo). Idempotente.",
        minPermission: "write",
        body: '{ "email": "contato@empresa.com" }',
      },
    ],
  },
  {
    key: "admins",
    label: "Administradores",
    endpoints: [
      {
        method: "GET",
        path: "/v1/admins",
        description: "Lista todos os administradores.",
        minPermission: "admin",
      },
      {
        method: "POST",
        path: "/v1/admins",
        description: "Cria um novo administrador.",
        minPermission: "admin",
        body: '{\n  "email": "novo@admin.com",\n  "password": "senhaForte123",\n  "name": "Novo Admin"\n}',
      },
      {
        method: "DELETE",
        path: "/v1/admins/{user_id}",
        description: "Remove role admin de um usuário.",
        minPermission: "admin",
      },
    ],
  },
];

const statusCodes = [
  { code: "200", label: "Sucesso", cls: "bg-ok-soft text-ok" },
  { code: "201", label: "Criado", cls: "bg-ok-soft text-ok" },
  { code: "400", label: "Erro de validação", cls: "bg-warn-soft text-warn" },
  { code: "401", label: "Chave inválida", cls: "bg-warn-soft text-warn" },
  { code: "403", label: "Sem permissão", cls: "bg-bad-soft text-bad" },
  { code: "404", label: "Não encontrado", cls: "bg-bad-soft text-bad" },
  { code: "405", label: "Método não permitido", cls: "bg-bad-soft text-bad" },
  { code: "409", label: "Conflito (duplicado)", cls: "bg-warn-soft text-warn" },
  { code: "429", label: "Limite de requisições", cls: "bg-warn-soft text-warn" },
  { code: "500", label: "Erro interno", cls: "bg-bad-soft text-bad" },
];

const mcpLevels = [
  { level: "read", label: "Leitura", cls: "bg-ok-soft text-ok" },
  { level: "write", label: "Escrita", cls: "bg-accent text-primary" },
  { level: "admin", label: "Admin", cls: "bg-bad-soft text-bad" },
] as const;

function ConnectedAppsSection() {
  const { data: apps, isLoading, isError } = useOAuthConnectedApps();
  const revoke = useRevokeOAuthApp();

  return (
    <section className={`${sectionCls} overflow-hidden`} aria-labelledby="oauth-apps-heading">
      <div className={sectionHeadCls}>
        <h2 id="oauth-apps-heading" className={h2Cls}>
          Apps conectados (OAuth)
        </h2>
      </div>
      {isLoading ? (
        <div className="py-6 text-center text-sm text-muted-foreground">Carregando...</div>
      ) : isError ? (
        <div className="py-6 text-center text-sm text-muted-foreground">
          Não foi possível carregar os apps conectados.
        </div>
      ) : !apps || apps.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          Nenhum app conectado. Conecte o Claude pela URL do MCP acima.
        </div>
      ) : (
        apps.map((app) => (
          <div
            key={app.clientId}
            className="flex items-center gap-4 border-b border-border/70 px-6 py-[15px] last:border-b-0 hover:bg-muted/40"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13.5px] font-semibold">
                {app.name || "App sem nome"}
              </div>
              <div className="mt-0.5 truncate text-[11.5px] text-muted-foreground">
                autorizado{" "}
                {formatDistanceToNow(new Date(app.grantedAt), { addSuffix: true, locale: ptBR })}
              </div>
            </div>
            <span className={`${pillCls} bg-accent text-primary`}>{permissionLabels.admin}</span>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-bad"
                  aria-label={`Revogar acesso de ${app.name || "app"}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Revogar acesso de {app.name || "app"}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    O app perde o acesso ao painel e precisa de nova autorização para voltar. Um
                    token já emitido pode valer até expirar (no máximo 1 hora).
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => revoke.mutate(app.clientId)}
                    className="bg-bad text-bad-foreground hover:bg-bad/90"
                  >
                    Revogar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ))
      )}
    </section>
  );
}

function McpSection() {
  const tools = OPERATIONS.filter((op) => !op.restOnly);
  return (
    <section className={`${sectionCls} px-6 py-[22px]`} aria-labelledby="mcp-heading">
      <div className="mb-3.5 flex items-center justify-between">
        <h2 id="mcp-heading" className={h2Cls}>
          Conexão MCP
        </h2>
        <span className="inline-flex h-[22px] items-center rounded-[7px] border border-border px-[9px] text-[11px] font-bold text-muted-foreground">
          Streamable HTTP
        </span>
      </div>
      <p className="mb-3 mt-0 text-[12.5px] leading-relaxed text-muted-foreground">
        Conecte agentes de IA (Claude, Cursor…) ao dashboard usando a mesma chave de API. As
        ferramentas visíveis seguem o nível da chave; criar ou remover administradores fica só na
        API REST. Prefira uma chave dedicada, com o menor nível necessário.
      </p>
      <CodeBlock code={MCP_URL} />
      <div className="mt-4 text-[12px] font-semibold">
        claude.ai, Claude Desktop e celular (sem chave)
      </div>
      <p className="mb-0 mt-1.5 text-[12.5px] leading-relaxed text-muted-foreground">
        Configurações → Conectores → Adicionar conector personalizado → cole a URL acima e deixe o
        OAuth Client ID em branco. O Claude abre o login do painel; entre com sua conta admin e
        autorize. A conexão tem acesso de administrador e aparece em &quot;Apps conectados&quot;
        abaixo, onde pode ser revogada.
      </p>
      <div className="mt-4 text-[12px] font-semibold">
        Claude Code e outros clientes (com chave)
      </div>
      <div className="mt-2">
        <CodeBlock
          code={`claude mcp add --transport http dashboard ${MCP_URL} \\
  --header "Authorization: Bearer lv_prod_sua_chave_aqui"`}
        />
      </div>
      <div className="mt-4 text-[12px] font-semibold">Outros clientes (.mcp.json)</div>
      <div className="mt-2">
        <CodeBlock
          code={JSON.stringify(
            {
              mcpServers: {
                dashboard: {
                  type: "http",
                  url: MCP_URL,
                  headers: { Authorization: "Bearer lv_prod_sua_chave_aqui" },
                },
              },
            },
            null,
            2,
          )}
        />
      </div>
      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        {mcpLevels.map(({ level, label, cls }) => (
          <div key={level} className="rounded-xl border border-border bg-muted/40 p-4">
            <div className={`${pillCls} mb-2 ${cls}`}>{label}</div>
            <ul className="m-0 list-none space-y-0.5 p-0 font-mono text-[11.5px] text-muted-foreground">
              {tools
                .filter((op) => op.level === level)
                .map((op) => (
                  <li key={op.name}>{op.name}</li>
                ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

export function ApiDocs() {
  const [moreOpen, setMoreOpen] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      <section className={`${sectionCls} px-6 py-[22px]`}>
        <div className="mb-3.5 flex items-center justify-between">
          <h2 className={h2Cls}>Base URL</h2>
          <span className="inline-flex h-[22px] items-center rounded-[7px] border border-border px-[9px] text-[11px] font-bold text-muted-foreground">
            v2.0
          </span>
        </div>
        <CodeBlock code={BASE_URL} />
        <p className="mb-0 mt-3 text-[12.5px] leading-relaxed text-muted-foreground">
          Toda requisição precisa do header{" "}
          <code className="rounded-[5px] border border-border bg-muted/60 px-[5px] py-px font-mono">
            x-api-key
          </code>{" "}
          (ou{" "}
          <code className="rounded-[5px] border border-border bg-muted/60 px-[5px] py-px font-mono">
            Authorization: Bearer
          </code>
          ) com sua chave. Erros voltam como{" "}
          <code className="rounded-[5px] border border-border bg-muted/60 px-[5px] py-px font-mono">
            {'{ "success": false, "error": "<codigo>" }'}
          </code>
          .
        </p>
      </section>

      <ApiKeysSection />

      <McpSection />

      <ConnectedAppsSection />

      <section className={`${sectionCls} overflow-hidden`}>
        <div className={sectionHeadCls}>
          <h2 className={h2Cls}>Endpoints</h2>
        </div>
        {resources.map((r) => (
          <div key={r.key}>
            <div className="px-6 pb-1.5 pt-3 text-[11px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
              {r.label}
            </div>
            {r.endpoints.map((ep) => (
              <EndpointRow key={`${ep.method}-${ep.path}`} {...ep} />
            ))}
          </div>
        ))}
      </section>

      <Collapsible open={moreOpen} onOpenChange={setMoreOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="outline" className="h-9 rounded-[9px] text-[12.5px] font-semibold">
            {moreOpen ? "Ver menos" : "Ver mais"}
            <ChevronDown
              className={`ml-1.5 h-3.5 w-3.5 transition-transform ${moreOpen ? "rotate-180" : ""}`}
            />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4 flex flex-col gap-4">
          <section className={`${sectionCls} px-6 py-[22px]`}>
            <h2 className={`${h2Cls} mb-3.5`}>Exemplo de requisição</h2>
            <CodeBlock
              code={`curl -X GET "${BASE_URL}/v1/leads" \\
  -H "x-api-key: lv_prod_sua_chave_aqui" \\
  -H "Content-Type: application/json"`}
            />
          </section>

          <section className={`${sectionCls} px-6 py-[22px]`}>
            <h2 className={`${h2Cls} mb-3.5`}>Níveis de permissão</h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="rounded-xl border border-border bg-muted/40 p-4">
                <div className={`${pillCls} mb-2 bg-ok-soft text-ok`}>Leitura</div>
                <p className="m-0 text-[12.5px] text-muted-foreground">
                  Apenas requisições <strong>GET</strong>. Ideal para dashboards e relatórios
                  externos.
                </p>
              </div>
              <div className="rounded-xl border border-border bg-muted/40 p-4">
                <div className={`${pillCls} mb-2 bg-accent text-primary`}>Escrita</div>
                <p className="m-0 text-[12.5px] text-muted-foreground">
                  <strong>GET + POST + PUT</strong>. Para automações que criam e atualizam dados.
                </p>
              </div>
              <div className="rounded-xl border border-border bg-muted/40 p-4">
                <div className={`${pillCls} mb-2 bg-bad-soft text-bad`}>Admin</div>
                <p className="m-0 text-[12.5px] text-muted-foreground">
                  <strong>Acesso total</strong> incluindo DELETE e gestão de administradores
                  (criar/remover admin só pela REST).
                </p>
              </div>
            </div>
          </section>

          <section className={`${sectionCls} px-6 py-[22px]`}>
            <h2 className={`${h2Cls} mb-3.5`}>Códigos de status HTTP</h2>
            <div className="grid grid-cols-2 gap-3 text-[12.5px] md:grid-cols-4">
              {statusCodes.map((s) => (
                <div key={s.code} className="flex items-center gap-2">
                  <span className={`${pillCls} ${s.cls}`}>{s.code}</span>
                  <span className="text-muted-foreground">{s.label}</span>
                </div>
              ))}
            </div>
          </section>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
